<template>
  <div class="manuscript-preview-page">
    <manuscript-preview class="preview" :novelId="novelId" :manuscriptList="manuscriptList" />
    <div class="footer">
      <div class="empty"></div>
      <button class="button" @click="onClickClose">{{ $t('common.close') }}</button>
      <div class="setting-container">
        <dropup-menu
          class="dropup-menu"
          :title="countTypeName"
          :content="wordCountInfo"
          :items="countTypeList"
          :styleObject="{ backgroundColor: 'var(--color-surface-muted)' }"
        />
        <hamburger-menu
          class="hamburger-menu"
          :items="hamburgerMenuList"
          :styleObject="{ backgroundColor: 'var(--color-surface-muted)' }"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import clone from "lodash/cloneDeep";
import ManuscriptPreview from "@/components/organisms/ManuscriptPreview.vue";
import DropupMenu, { Item } from "@/components/molecules/DropupMenu.vue";
import HamburgerMenu from "@/components/molecules/HamburgerMenu.vue";
import { Manuscript, ManuscriptSetting, Plan } from "@/lib/models";
import { Dialog } from "@/lib/utils";
import ManuscriptSettingsDialog, { ManuscriptSettingsDialogProps } from "@/components/ui/ManuscriptSettingsDialog.vue";
import ColorThemeDialog from "@/components/ui/ColorThemeDialog.vue";
import { convertRubyToHtml, breakVerticalLines } from "@/lib/utils/verticalLineBreak";

export default Vue.extend<Data, Methods, Computed, Props>({
  // NOTE: metaタグの設定
  metaInfo: {
    meta: [
      {
        name: "robots",
        content: "none",
      },
    ],
  },
  components: {
    ManuscriptPreview,
    DropupMenu,
    HamburgerMenu,
  },
  props: {
    novelId: String,
  },
  data() {
    const { onSelectCountType, showManuscriptSettings, showColorTheme } = this as any;

    const countTypeList: Item[] = [
      {
        name: this.$t('manuscriptPreviewPage.countTypes.allCharacters') as string,
        value: "onlyCharacter",
        callback() {
          onSelectCountType(this.value);
        },
      },
      {
        name: this.$t('manuscriptPreviewPage.countTypes.withSpaces') as string,
        value: "addBlank",
        callback() {
          onSelectCountType(this.value);
        },
      },
      {
        name: this.$t('manuscriptPreviewPage.countTypes.withNewlines') as string,
        value: "addNewLine",
        callback() {
          onSelectCountType(this.value);
        },
      },
      {
        name: this.$t('manuscriptPreviewPage.countTypes.withSpacesAndNewlines') as string,
        value: "all",
        callback() {
          onSelectCountType(this.value);
        },
      },
    ];

    const hamburgerMenuList: Item[] = [
      {
        name: this.$t('manuscriptPreviewPage.settingsMenu.manuscriptSettings') as string,
        callback() {
          showManuscriptSettings();
        },
      },
      {
        name: this.$t('manuscriptPreviewPage.settingsMenu.colorTheme') as string,
        callback() {
          showColorTheme();
        },
      },
    ];

    return {
      countTypeList,
      hamburgerMenuList,
    };
  },
  computed: {
    manuscriptList() {
      return this.$store.getters["manuscriptModule/manuscriptList"];
    },
    allContentLength() {
      const manuscriptList = this.manuscriptList as Manuscript[];
      if (manuscriptList.length === 1) {
        return this.contentLength(manuscriptList[0].content);
      }
      return manuscriptList.reduce((acc, cur) => acc + this.contentLength(cur.content), 0);
    },
    contentLength() {
      return (content) => {
        if (!content) return 0;

        let rubyLength = 0;

        // eslint-disable-next-line no-control-regex
        const rubyRegExp = new RegExp("[|｜][^|｜\r\n]*?《.*?》", "gu");
        const textWithRuby = content.match(rubyRegExp);
        if (textWithRuby) {
          textWithRuby.forEach((text) => {
            const onlyRubyRegExp = new RegExp("《.*?》$");
            const textOnlyRuby = text.match(onlyRubyRegExp);
            rubyLength += textOnlyRuby![0].length + 1; // 親文字の縦棒をカウントから除くための+1
          });
        }

        let contentLength = 0;
        switch (this.countType) {
          case "addBlank":
            contentLength = content.replace(/\n/g, "").length;
            break;
          case "addNewLine":
            contentLength = content.replace(/[^\S\n]/g, "").length;
            break;
          case "all":
            contentLength = content.length;
            break;
          case "onlyCharacter":
          default:
            contentLength = content.replace(/\s+|\n/g, "").length;
            break;
        }

        return contentLength - rubyLength;
      };
    },
    allPageCount() {
      const manuscriptList = this.manuscriptList as Manuscript[];
      if (manuscriptList.length === 1) {
        return this.pageCount(manuscriptList[0].content);
      }
      return manuscriptList.reduce((acc, cur) => acc + this.pageCount(cur.content), 0);
    },
    pageCount() {
      return (content) => {
        const clonedContent = clone(content) as string;

        /** 原稿の設定情報を取得 */
        const { wordCountOnVertical, lineCountPerPage } = this.manuscriptSetting as ManuscriptSetting;

        /** プレビューと同じ行分割ロジック（追い出し禁則つき）で行数を計算する */
        let lineCount = 0;
        clonedContent.split("\n").forEach((paragraph) => {
          const rubyHtml = convertRubyToHtml(paragraph);
          lineCount += breakVerticalLines(rubyHtml, wordCountOnVertical).length || 1; // 空行も1行と数える
        });
        return Math.ceil(lineCount / lineCountPerPage);
      };
    },
    countType() {
      return this.$store.getters["manuscriptModule/countType"];
    },
    countTypeName() {
      const countTypeValue = this.countType;
      const countType = (this.countTypeList as Item[]).find((countType) => countType.value === countTypeValue);
      const countTypeName = countType ? countType.name : "";

      /** 原稿の設定情報を取得 */
      const { wordCountOnVertical, lineCountPerPage } = this.manuscriptSetting as ManuscriptSetting;

      return this.$t('manuscriptPreviewPage.countFormat', { 
        countTypeName, 
        vertical: wordCountOnVertical, 
        horizontal: lineCountPerPage 
      });
    },
    wordCountInfo() {
      const contentLength = this.allContentLength;
      const pageCount = this.allPageCount;

      return this.$t('manuscriptPreviewPage.wordCountInfo', { 
        count: contentLength, 
        pages: pageCount 
      });
    },
    plan() {
      return this.$store.getters["billingModule/plan"] as Plan;
    },
    manuscriptSetting() {
      return this.$store.getters["novelModule/manuscriptSetting"](this.novelId) as ManuscriptSetting;
    },
  },
  methods: {
    onClickClose() {
      this.$router.push({ name: "editor" });
    },
    onSelectCountType(countTypeValue) {
      this.$store.dispatch("manuscriptModule/selectCountType", countTypeValue);
    },
    showManuscriptSettings() {
      const { novelId, plan } = this;
      const manuscriptSettingsDialog = new Dialog(ManuscriptSettingsDialog);
      const data: ManuscriptSettingsDialogProps = {
        novelId,
        isChangeable: Boolean(plan),
        positive: this.$t('common.save') as string,
        positiveCallback: async () => {},
      };
      manuscriptSettingsDialog.show(data);
    },
    showColorTheme() {
      const colorThemeDialog = new Dialog(ColorThemeDialog);
      colorThemeDialog.show();
    },
  },
});

interface Props {
  novelId: string;
}

interface Data {
  countTypeList: Item[];
  hamburgerMenuList: Item[];
}

interface Methods {
  onClickClose: () => void;
  onSelectCountType(countTypeValue: any): void;
  showManuscriptSettings: () => void;
  showColorTheme(): void;
}

interface Computed {
  manuscriptList: Manuscript[];
  allContentLength: number;
  contentLength: (content: string) => number;
  allPageCount: number;
  pageCount: (content: string) => number;
  countType: string;
  countTypeName: string;
  wordCountInfo: string;
  plan: Plan;
  manuscriptSetting: ManuscriptSetting;
}
</script>

<style lang="scss" scoped>
@import "@/style-common.scss";
.manuscript-preview-page {
  width: 100%;
  height: 100vh;
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: 1fr 70px;

  .footer {
    box-sizing: border-box;
    position: relative;
    display: flex;
    justify-content: space-between;
    align-items: center;
    background-color: var(--color-surface-muted);
    border-top: 3px solid var(--color-border);

    .empty {
      width: 250px;
    }

    .button {
      height: 50px;
      width: 250px;
      border-radius: var(--radius-rounded);
      background-color: var(--color-background-normal);
      color: var(--color-text-normal);
      border: 1px solid var(--color-border);
    }

    .setting-container {
      height: 100%;
      display: flex;
      align-items: center;

      .dropup-menu {
        box-sizing: border-box;
        border-left: 1px solid var(--color-border);
      }

      .hamburger-menu {
        width: 50px;
        background-color: var(--color-bg-dark);
      }
    }
  }
}
</style>
